import { useMemo } from 'react';
import useSWR from 'swr';
import { BaseAPI } from 'src/providers/base-api';
import { AuthType } from 'src/constant/common.interface';
import { useAppSelector } from 'src/store/store';
import { selectUser } from 'src/reducers/authSlice';
import { usePermissionsV2 } from './usePermissionsV2';

// Feature flag for new RBAC system
const USE_NEW_RBAC = process.env.REACT_APP_FEATURE_NEW_RBAC === 'true';

// Fetcher function for SWR (legacy)
const permissionFetcher = async (roleId: number) => {
    const response = await BaseAPI.getUserRoleDatabyRoleID(roleId, () => { });
  if (response.status === 200 && response?.data) {
    return response.data;
  }
  throw new Error('Failed to fetch permissions');
};

const DEBUG_LOGS = false;

/**
 * usePermissions - Main permissions hook with feature flag support
 *
 * This hook automatically switches between the legacy and new v2 RBAC system
 * based on the REACT_APP_FEATURE_NEW_RBAC environment variable.
 *
 * @param featureName - Feature/entity name(s) to check permissions for
 * @param grantFullAccessIfMissing - If true, grant full access when feature not found
 * @returns { authorisation, isLoading, error }
 */
export const usePermissions = (featureName: string | string[], grantFullAccessIfMissing = false) => {
  // Always call all hooks unconditionally at the top (Rules of Hooks)
  const v2Result = usePermissionsV2(featureName, grantFullAccessIfMissing);

  // ===== LEGACY IMPLEMENTATION BELOW =====
  // Try Redux first, fallback to sessionStorage for backward compatibility
  const reduxUser = useAppSelector(selectUser);

  // Fallback to sessionStorage if Redux state is not populated
  let userDetail = reduxUser;
  if (!userDetail) {
    const userDetailString = sessionStorage.getItem('UserDetail');
    userDetail = userDetailString ? JSON.parse(userDetailString) : null;
  }

  // Support both roles array and singular role object
  const roleId = userDetail?.roles?.[0]?.id || userDetail?.role?.id;

  if (!roleId) {
    if (DEBUG_LOGS) console.warn('usePermissions: No roleId found in UserDetail', userDetail);
  }

  // SWR automatically caches based on the key ('permissions', roleId)
    const { data, error, isLoading: swrIsLoading } = useSWR(
        roleId ? ['permissions', roleId] : null,
        () => permissionFetcher(roleId),
        {
    revalidateOnFocus: false, // Don't re-fetch when switching tabs
            revalidateIfStale: true,  // Default: Use cached data but refresh if stale
            revalidateOnMount: true,  // Default: Refresh when mounting
            dedupingInterval: 60000,   // Cache for 60 seconds
            shouldRetryOnError: false, // Don't retry on 403/auth errors — prevents request storms
        }
    );

  // Consider loading if roleId is not yet available (Redux/sessionStorage not hydrated)
  // or if SWR is actively loading
  const isLoading = !roleId || swrIsLoading;

  if (error) {
    console.error('usePermissions: SWR fetch error', error);
  }

  const featureNames = Array.isArray(featureName) ? featureName : [featureName];

  // Helper to normalize strings for matching (lowercase, no spaces/underscores/dashes)
  const normalize = (s: string) =>
    (s || '')
      .toString()
      .toLowerCase()
      .replace(/[\s_-]/g, '');

  // Memoize authorisation to prevent creating new object on every render
  // This is critical to prevent infinite loops in components that use authorisation as a dependency
  const authorisation = useMemo<AuthType>(() => {
    const normFeatureNames = featureNames.map(normalize);

    if (DEBUG_LOGS) console.log(`usePermissions checking for [${featureNames.join(', ')}]`, { roleId, hasData: !!data });

    let auth: AuthType = {
      isfullAccess: false,
      isCreate: false,
      isView: false,
      isEdit: false,
      isDelete: false,
      isManageAll: false,
      isShow: false
    };

    if (data) {
      if (DEBUG_LOGS) console.log('usePermissions data (subCategory):', data.subCategory);

      const filteredSubCategories = data.subCategory?.filter(
        (sub: any) => {
          // Direct sub-category match — always include
          if (normFeatureNames.includes(normalize(sub.subCategoryRefName))) return true;
          if (normFeatureNames.includes(normalize(sub.refName))) return true;
          if (normFeatureNames.includes(normalize(sub.name))) return true;

          // Category-level match — only include if this entry has no specific
          // sub_category_ref_name (i.e. it IS the category, not a sibling sub-category).
          // Without this guard, usePermissions('Project Management') would match
          // Tasks/Tickets/etc. entries that share category_ref_name 'Project_management',
          // granting false isEdit/isCreate to users who only have those siblings.
          if (normFeatureNames.includes(normalize(sub.categoryRefName))) {
            const subCatRef = normalize(sub.subCategoryRefName);
            return !subCatRef || normFeatureNames.includes(subCatRef);
          }

          return false;
        }
      );

      if (grantFullAccessIfMissing && (!filteredSubCategories || filteredSubCategories.length === 0)) {
        auth = { isfullAccess: true, isCreate: true, isView: true, isEdit: true, isDelete: true, isManageAll: false, isShow: true };
      } else {
        filteredSubCategories?.forEach((element: any) => {
          const method = normalize(element.method);
          if (method === 'fullaccess') {
            auth.isfullAccess = true;
            auth.isCreate = true;
            auth.isView = true;
            auth.isEdit = true;
            auth.isDelete = true;
            auth.isShow = true;
          } else if (method === 'manageall') {
            auth.isManageAll = true;
            auth.isShow = true;
          } else if (method === 'create') {
            auth.isCreate = true;
            auth.isShow = true;
          } else if (element.method === 'View') {
            auth.isView = true;
            auth.isShow = true;
          } else if (element.method === 'Edit') {
            auth.isEdit = true;
            auth.isShow = true;
          } else if (element.method === 'Delete') {
            auth.isDelete = true;
            auth.isShow = true;
          }
        });
      }
    }

    return auth;
  }, [data, featureName, grantFullAccessIfMissing]);

  // If feature flag is enabled, return v2 results
  if (USE_NEW_RBAC) {
    return v2Result;
  }

  return { authorisation, isLoading, error };
};
